"use client";

import React from "react";
import { Loader, Send, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function QuoteFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center space-y-4 bg-green-50/50 border border-green-100 rounded-lg animate-in fade-in zoom-in duration-500">
        <div className="h-16 w-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-2">
          <CircleCheck className="h-8 w-8" />
        </div>
        <h3 className="text-2xl font-serif text-green-800">Richiesta Inviata!</h3>
        <p className="text-green-700 max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-green-200 text-green-700 hover:bg-green-50 hover:text-green-800">
          Invia una nuova richiesta
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="name" rules={{
        required: "Il nome è obbligatorio"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Il tuo Nome <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input placeholder="Mario Rossi" {...field} className="bg-white/50" />
              </FormControl>
              <FormDescription>Inserisci il nome completo.</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "L'email è obbligatoria",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Inserisci un indirizzo email valido"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Indirizzo Email <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input type="email" placeholder="mario@example.com" {...field} className="bg-white/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="phone" rules={{
        required: "Il numero di telefono è obbligatorio"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Numero di Telefono <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+39 123 456 7890" {...field} className="bg-white/50" />
              </FormControl>
              <FormDescription>Inserisci il numero con prefisso internazionale.</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="product_type" rules={{
        required: "Seleziona un tipo di prodotto"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Tipo di Prodotto <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <div className="relative">
                  <select className="flex h-9 w-full rounded-md border border-input bg-white/50 px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50" {...field} value={field.value || ""}>
                    <option value="" disabled>Seleziona il prodotto</option>
                    <option value="Cannoli">Cannoli</option>
                    <option value="Cassata">Cassata</option>
                    <option value="Pasta di Mandorla">Pasta di Mandorla</option>
                    <option value="Torta su Ordinazione">Torta su Ordinazione</option>
                    <option value="Altro">Altro</option>
                  </select>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="details" rules={{
      required: "I dettagli sono obbligatori"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">Dettagli della Richiesta <span className="text-destructive">*</span></FormLabel>
            <FormControl>
              <textarea className="flex min-h-[120px] w-full rounded-md border border-input bg-white/50 px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 resize-y" placeholder="Specifiche di gusti, quantità, eventuali allergeni, decorazioni, ecc." {...field} />
            </FormControl>
            <FormDescription>Più informazioni fornisci, più preciso sarà il preventivo.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="preferred_contact_whatsapp" render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-input/50 p-4 bg-white/30">
            <FormControl>
              <input type="checkbox" checked={field.value} onChange={field.onChange} className="h-4 w-4 mt-1 rounded border-primary text-primary focus:ring-primary accent-primary" />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-base font-medium text-foreground cursor-pointer">
                Preferisco essere contattato via WhatsApp
              </FormLabel>
              <FormDescription>
                Se selezionato, ti contatteremo direttamente su WhatsApp al numero indicato.
              </FormDescription>
            </div>
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 text-red-800">
          <CircleAlert className="h-5 w-5 shrink-0 mt-0.5" />
          <p>{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-lg font-medium bg-[hsl(41,48%,56%)] hover:bg-[hsl(41,48%,50%)] text-white shadow-md transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 h-5 w-5" />
            Invio in corso...
          </> : <>
            Invia Richiesta su WhatsApp
            <Send className="ml-2 h-5 w-5" />
          </>}
      </Button>
    </Form>;
}
export default function RequestQuoteSection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="richiedi-un-preventivo" className="py-20 md:py-28 bg-background relative overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-40 pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/cream-paper.png')] mix-blend-multiply"></div>
      
      {}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[hsl(41,48%,56%)] to-transparent opacity-30"></div>
      <div className="absolute -top-24 -right-24 w-64 h-64 bg-[hsl(41,48%,56%)]/5 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-[hsl(24,35%,18%)]/5 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div ref={ref} className={`max-w-3xl mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="text-center mb-12 space-y-4">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-primary">
              Richiedi un Preventivo
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed">
              Compila il modulo sottostante per ricevere una proposta personalizzata per le tue occasioni speciali. 
              Dal cannolo alla torta nuziale, realizziamo i tuoi desideri dolciari.
            </p>
          </div>

          <div className="bg-card/80 backdrop-blur-sm border border-border/60 rounded-xl shadow-xl p-6 md:p-10 relative overflow-hidden">
            {}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[hsl(41,48%,56%)]/20 via-[hsl(41,48%,56%)] to-[hsl(41,48%,56%)]/20"></div>
            
            <FormProvider formId="quote_form_creations_gallery" sectionName="request-quote-section" defaultValues={{
            name: "",
            email: "",
            phone: "",
            product_type: "",
            details: "",
            preferred_contact_whatsapp: false
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            product_type: {
              required: true
            },
            details: {
              required: true
            }
          }} submitText="Invia Richiesta su WhatsApp" successMessage="Grazie! Abbiamo ricevuto la tua richiesta. Ti contatteremo presto su WhatsApp o via email con il tuo preventivo.">
              <QuoteFormContent />
            </FormProvider>
          </div>
          
          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground">
              Preferisci chiamarci direttamente? <br className="md:hidden" />
              <span className="font-medium text-primary">Tel: +39 0925 86986</span>
            </p>
          </div>
        </div>
      </div>
    </section>;
}